import React, {
  Fragment,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { withTranslation } from 'react-i18next';
import { compose } from 'recompose';
import PropTypes from 'prop-types';
import { Map, TileLayer } from 'react-leaflet';
import * as L from 'leaflet';
import { GestureHandling } from 'leaflet-gesture-handling';

import { TemplateStopType } from 'services/enums/TemplateStopType';
import {
  IconService,
  templateStopConnectionColor,
  unassignedTemplateStopIconColor,
} from 'services/icon/iconService';
import TemplateStopOverlappingMarkerCluster from 'components/Maps/MapComponents/Marker/TemplateStopOverlappingMarkerCluster';
import TemplateStopTemplateStopMarkerCluster from 'components/Maps/MapComponents/Marker/TemplateStopTemplateStopMarkerCluster';
import TemplateStopTemplateMarkerCluster from 'components/Maps/MapComponents/Marker/TemplateStopTemplateMarkerCluster';
import PolylineCluster from 'components/Maps/MapComponents/Marker/PolylineCluster';
import {
  filterTemplatesForDuplicate,
  filterTemplateStopsForCoords,
  filterTemplateStopsForDuplicate,
  filterTemplateStopTemplatesForCoords,
  getBoundingBox,
  getBoundingBoxCoords,
  getElementsWithSamePositionTemplateStop,
  getNumTemplateStopsOnMap,
  getPolyLinesFromTemplateStop,
  getPolyLinesFromTemplateStopTemplates,
} from 'services/util/mapUtils';
import useMapControls from 'hooks/useMapControls';
import {
  MapControls,
  MarkerCreateFuncs,
  OverlappingMarkerCreateFuncs,
} from '../../MapComponents';

const BaseTemplateStopSelectionMap = (props) => {
  const {
    className,
    templates,
    displayControls,
    onSelectAssignedTemplateStops,
    onSelectUnassignedTemplateStops,
    selectedAssignedTemplateStops,
    selectedUnassignedTemplateStops,
    unselectedUnassignedTemplateStops,
  } = props;

  const [hoveredTemplateStop, setHoveredTemplateStop] = useState(null);

  const highlightTemplateStopRelations = useCallback(
    (templateStop) => {
      // we don't want a rerender, if the template stop is the same
      if (
        hoveredTemplateStop === null ||
        templateStop.id !== hoveredTemplateStop.id
      ) {
        setHoveredTemplateStop(templateStop);
      }
    },
    [hoveredTemplateStop]
  );

  const hideTemplateStopRelations = useCallback(() => {
    setHoveredTemplateStop(null);
  }, []);

  const map = useRef(null);

  const templateStops = templates.reduce(
    (acc, template) => acc.concat(template.stops),
    []
  );
  const polyLinesPickupDeliveryConnections = hoveredTemplateStop
    ? getPolyLinesFromTemplateStop(
        unselectedUnassignedTemplateStops
          .concat(selectedUnassignedTemplateStops)
          .concat(templateStops),
        hoveredTemplateStop
      )
    : [];

  function getSelectedTemplateStopMarkerAndAddToMcg(
    template,
    templateStop,
    stopNumber,
    mcg,
    t
  ) {
    return MarkerCreateFuncs.getTemplateStopMarkerTemplateStop({
      template,
      templateStop,
      stopNumber,
      mcg,
      t,
      onSelectTemplateStop: (stop) =>
        onSelectAssignedTemplateStops({
          templateStops: [stop],
          unselect: true,
        }),
      stopType: templateStop.stopType,
      isSelected: true,
      onHoverStart: highlightTemplateStopRelations,
      onHoverEnd: hideTemplateStopRelations,
    });
  }

  function getSelectedUnassignedTemplateStopMarkerAndAddToMcg(
    templateStop,
    mcg,
    t
  ) {
    return MarkerCreateFuncs.getUnassignedTemplateStopMarker({
      templateStop,
      mcg,
      t,
      onSelectTemplateStop: (stop) =>
        onSelectUnassignedTemplateStops({
          templateStops: [stop],
          unselect: true,
        }),
      color: unassignedTemplateStopIconColor,
      innerColor: '#000000',
      onHoverStart: highlightTemplateStopRelations,
      onHoverEnd: hideTemplateStopRelations,
      stopType: templateStop.stopType,
      isSelected: true,
    });
  }

  function getUnassignedTemplateStopMarkerAndAddToMcg(templateStop, mcg, t) {
    return MarkerCreateFuncs.getUnassignedTemplateStopMarker({
      templateStop,
      mcg,
      t,
      onSelectTemplateStop: (stop) =>
        onSelectUnassignedTemplateStops({ templateStops: [stop] }),
      color: unassignedTemplateStopIconColor,
      innerColor: '#000000',
      onHoverStart: highlightTemplateStopRelations,
      onHoverEnd: hideTemplateStopRelations,
      stopType: templateStop.stopType,
      isSelected: false,
    });
  }

  function getTemplateStopMarkerAndAddToMcg(
    template,
    templateStop,
    stopNumber,
    mcg,
    t
  ) {
    return MarkerCreateFuncs.getTemplateStopMarkerTemplateStop({
      template,
      templateStop,
      stopNumber,
      mcg,
      t,
      onSelectTemplateStop: (stop) =>
        onSelectAssignedTemplateStops({ templateStops: [stop] }),
      stopType: templateStop.stopType,
      isSelected: false,
      onHoverStart: highlightTemplateStopRelations,
      onHoverEnd: hideTemplateStopRelations,
    });
  }

  const templatesWithStopsWithCoords =
    filterTemplateStopTemplatesForCoords(templates);

  const unselectedUnassignedTemplateStopsWithCoords =
    filterTemplateStopsForCoords(unselectedUnassignedTemplateStops);

  const selectedUnassignedTemplateStopsWithCoords =
    filterTemplateStopsForCoords(selectedUnassignedTemplateStops);

  const selectedAssignedTemplateStopsWithCoords = filterTemplateStopsForCoords(
    selectedAssignedTemplateStops
  );

  const templateStopsOnMap = getNumTemplateStopsOnMap(
    templatesWithStopsWithCoords,
    unselectedUnassignedTemplateStopsWithCoords,
    selectedAssignedTemplateStopsWithCoords.concat(
      selectedUnassignedTemplateStopsWithCoords
    )
  );
  const {
    useCluster,
    setUseCluster,
    useDuplicateSpiderfy,
    setUseDuplicateSpiderfy,
    highLiteDuplicates,
    setHighLiteDuplicates,
  } = useMapControls(templateStopsOnMap, false, false);

  const polyLines = getPolyLinesFromTemplateStopTemplates(
    templatesWithStopsWithCoords
  );

  const overlappingElementGroups = useDuplicateSpiderfy
    ? getElementsWithSamePositionTemplateStop(
        templatesWithStopsWithCoords,
        unselectedUnassignedTemplateStopsWithCoords.concat(
          selectedUnassignedTemplateStopsWithCoords
        ),
        [],
        selectedAssignedTemplateStopsWithCoords
          .concat(selectedUnassignedTemplateStopsWithCoords)
          .map((stop) => stop.id)
      )
    : [];
  const keysOfOverlappingElements = overlappingElementGroups.map(
    (g) => g[0].key
  );

  const filteredTemplates = filterTemplatesForDuplicate(
    templatesWithStopsWithCoords,
    keysOfOverlappingElements
  );
  const filteredUnassignedTemplateStops = filterTemplateStopsForDuplicate(
    unselectedUnassignedTemplateStopsWithCoords,
    keysOfOverlappingElements
  );
  const filteredSelectedUnassignedTemplateStops =
    filterTemplateStopsForDuplicate(
      selectedUnassignedTemplateStopsWithCoords,
      keysOfOverlappingElements
    );
  const filteredSelectedAssignedTemplateStops = filterTemplateStopsForDuplicate(
    selectedAssignedTemplateStopsWithCoords,
    keysOfOverlappingElements
  );

  L.Map.addInitHook('addHandler', 'gestureHandling', GestureHandling);

  const fitMapBoundsToFG = useCallback(() => {
    const coordinates = getBoundingBoxCoords(polyLines, templateStops);
    if (coordinates.length <= 1) return;
    const boundingBox = getBoundingBox(coordinates);
    map.current.leafletElement.fitBounds(boundingBox, { padding: [30, 30] });
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    fitMapBoundsToFG();
  }, [fitMapBoundsToFG]);

  return (
    <Fragment>
      <Map
        boundsOptions={{ padding: [10, 10] }}
        center={[50, 10.5]}
        className={className}
        gestureHandling
        ref={map}
        zoom={6}
        zoomSnap={0.25}
      >
        <TileLayer
          attribution='&amp;copy <a href="http://osm.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <Fragment>
          {filteredTemplates.map((template, index) => (
            <Fragment key={index}>
              <TemplateStopTemplateMarkerCluster
                getClusterIcon={(template) =>
                  IconService.getMarkerClusterIcon(template.color, template.id)
                }
                getTemplateStopMarkerAndAddToMcg={
                  getTemplateStopMarkerAndAddToMcg
                }
                key={template.id}
                polyline={polyLines[index]}
                template={template}
                templateStops={template.stops.filter(
                  (stop) =>
                    !filteredSelectedAssignedTemplateStops.some(
                      (selectedStop) => selectedStop.id === stop.id
                    )
                )}
                useCluster={useCluster}
              />
              <TemplateStopTemplateMarkerCluster
                getClusterIcon={(template) =>
                  IconService.getMarkerClusterIcon(template.color, template.id)
                }
                getTemplateStopMarkerAndAddToMcg={
                  getSelectedTemplateStopMarkerAndAddToMcg
                }
                key={template.id + '_select'}
                polyline={polyLines[index]}
                template={template}
                templateStops={template.stops.filter((stop) =>
                  filteredSelectedAssignedTemplateStops.some(
                    (selectedStop) => selectedStop.id === stop.id
                  )
                )}
                useCluster={useCluster}
              />
            </Fragment>
          ))}
        </Fragment>
        {[TemplateStopType.Pickup, TemplateStopType.Delivery].map(
          (stopType) => (
            <Fragment key={stopType}>
              <Fragment>
                <TemplateStopTemplateStopMarkerCluster
                  clusterIconColor={unassignedTemplateStopIconColor}
                  getClusterIcon={(iconColor) =>
                    IconService.getMarkerClusterIcon(iconColor)
                  }
                  getTemplateStopMarkerAndAddToMcg={
                    getUnassignedTemplateStopMarkerAndAddToMcg
                  }
                  templateStops={filteredUnassignedTemplateStops.filter(
                    (templateStop) => templateStop.stopType === stopType
                  )}
                  useCluster={useCluster}
                />
              </Fragment>
              <Fragment>
                <TemplateStopTemplateStopMarkerCluster
                  clusterIconColor={unassignedTemplateStopIconColor}
                  getClusterIcon={(iconColor) =>
                    IconService.getMarkerClusterIcon(iconColor)
                  }
                  getTemplateStopMarkerAndAddToMcg={
                    getSelectedUnassignedTemplateStopMarkerAndAddToMcg
                  }
                  templateStops={filteredSelectedUnassignedTemplateStops.filter(
                    (templateStop) => templateStop.stopType === stopType
                  )}
                  useCluster={false}
                />
              </Fragment>
            </Fragment>
          )
        )}
        {overlappingElementGroups.map((elementGroup, index) => (
          <TemplateStopOverlappingMarkerCluster
            getClusterIcon={(overlappingElements, highlightDuplicates) =>
              OverlappingMarkerCreateFuncs.getForAddTemplateStopToTemplateMap(
                overlappingElements,
                highlightDuplicates,
                () => {
                  return unassignedTemplateStopIconColor;
                }
              )
            }
            getTemplateStopMarkerAndAddToMcg={(
              template,
              templateStop,
              stopNumber,
              mcg,
              t,
              selected
            ) =>
              selected
                ? getSelectedTemplateStopMarkerAndAddToMcg(
                    template,
                    templateStop,
                    stopNumber,
                    mcg,
                    t
                  )
                : getTemplateStopMarkerAndAddToMcg(
                    template,
                    templateStop,
                    stopNumber,
                    mcg,
                    t
                  )
            }
            getUnassignedTemplateStopStopMarkerAndAddToMcg={(
              templateStop,
              mcg,
              t,
              selected
            ) =>
              selected
                ? getSelectedUnassignedTemplateStopMarkerAndAddToMcg(
                    templateStop,
                    mcg,
                    t
                  )
                : getUnassignedTemplateStopMarkerAndAddToMcg(
                    templateStop,
                    mcg,
                    t
                  )
            }
            highLiteDuplicates={highLiteDuplicates}
            key={index}
            overlappingElements={elementGroup}
          />
        ))}
        <Fragment>
          <PolylineCluster
            clusterIconColor={templateStopConnectionColor}
            getClusterIcon={(iconColor) =>
              IconService.getMarkerClusterIcon(iconColor)
            }
            polylines={polyLinesPickupDeliveryConnections}
            useCluster={false}
          />
        </Fragment>
      </Map>
      {displayControls && (
        <div>
          <MapControls
            highLiteDuplicates={highLiteDuplicates}
            setHighLiteDuplicates={setHighLiteDuplicates}
            setUseCluster={setUseCluster}
            setUseDuplicateSpiderfy={setUseDuplicateSpiderfy}
            useCluster={useCluster}
            useDuplicateSpiderfy={useDuplicateSpiderfy}
          />
        </div>
      )}
    </Fragment>
  );
};

BaseTemplateStopSelectionMap.displayName = 'BaseTemplateStopSelectionMap';

BaseTemplateStopSelectionMap.propTypes = {
  className: PropTypes.string,
  displayControls: PropTypes.bool,
  t: PropTypes.func.isRequired,
  templates: PropTypes.array.isRequired,
  onSelectAssignedTemplateStops: PropTypes.func.isRequired,
  onSelectUnassignedTemplateStops: PropTypes.func.isRequired,
  selectedAssignedTemplateStops: PropTypes.array.isRequired,
  selectedUnassignedTemplateStops: PropTypes.array.isRequired,
  unselectedUnassignedTemplateStops: PropTypes.array.isRequired,
};

export default compose(withTranslation())(BaseTemplateStopSelectionMap);
